/**
 * The `/_data/` transport's ERROR frame — the ONE contract both legs read.
 *
 * THE CLASS IT CLOSES. A loader that throws `new Response(null, {status: 404})`
 * is answered correctly on the SSR leg: React Router's static handler files the
 * throw under `staticContext.errors`, `statusCode` becomes 404, and the route's
 * `ErrorBoundary` renders the not-found page. The CLIENT-navigation leg went
 * through `/_data/:routeId`, which read `loaderData?.[routeId]` and answered
 * `200` with a body of `null` — the loader's verdict was not in the payload it
 * returned, so it could not be in the answer. The browser then handed `null` to
 * a route whose component destructures `useLoaderData()`, and the reader got a
 * `TypeError` in the feature error boundary instead of the 404 page. Same URL,
 * same loader, two different answers, decided by whether the reader arrived by
 * typing the URL or by clicking a link.
 *
 * WHY A FRAME AND NOT A BARE STATUS. The status alone loses `statusText` and
 * the `data` a loader attaches to its `Response`, both of which the SSR leg
 * DOES deliver to the boundary (`isRouteErrorResponse(e)` exposes all three).
 * A client leg that dropped them would render a *different* error page from the
 * server for the same failure — a subtler version of the same defect.
 *
 * WHY IT LIVES HERE. This module is client-safe and imported by BOTH legs —
 * `server/bff.ts` writes the frame, `app/entry-client.tsx` reads it — the same
 * edge `bff-route.ts` already takes to `bff-schemas.ts`. One module means there
 * is no second spelling of the wire shape to drift: a field added here is a
 * compile error on whichever side does not yet carry it.
 *
 * ⛔ `decodeDataRouteErrorFrame` is TOTAL and never throws. A non-2xx body may
 * be something else entirely — Hono's own `{error: 'Internal server error'}`
 * catch-all, a proxy's HTML error page, a truncated response — and the client
 * must still fail as an HTTP error with the status it actually received rather
 * than crash while parsing the explanation. `undefined` means "not one of ours",
 * never "no error".
 *
 * ⛔ AND SO IS THE MINT — for the same reason, with the opposite answer. Both
 * halves take a status they did not choose (the mint copies a loader's
 * `ErrorResponse`; the decoder reads an untrusted body), and the frame they
 * produce is fed to `new Response(body, {status, statusText})` on the client,
 * which THROWS on a status outside 200–599 and on a reason phrase carrying a
 * newline or a non-Latin-1 character. The decoder REJECTS a frame it cannot
 * trust, because it has something better to fall back on — the transport's own
 * HTTP status. The mint has no fallback, so it COERCES: an unexpressible status
 * degrades to 500 and a reason phrase is reduced to the bytes HTTP allows. The
 * asymmetry is deliberate and is argued at each definition.
 */

/** The frame's self-identifying tag. See {@link decodeDataRouteErrorFrame}. */
export const DATA_ROUTE_ERROR_KIND = 'data-route-error';

/**
 * The statuses a refusal can be spelled in. A frame IS a refusal: a 2xx would
 * turn a failure into a success on the client (`res.ok`), and a 3xx is a
 * redirect, which the static handler answers with a real `Response` long before
 * this module is reached.
 */
const REFUSAL_STATUS_MIN = 400;
const REFUSAL_STATUS_MAX = 599;

/** What a refusal degrades to when it cannot be spelled as one. */
const UNSPELLABLE_REFUSAL_STATUS = 500;

/**
 * Can this status BE a refusal? The single spelling of the range, shared by the
 * mint below, the decoder below it, and the client's own raw-status fallback
 * (`app/entry-client.tsx`) — three places that must not disagree about which
 * statuses are legal to rethrow as a `Response`.
 */
export function isDataRouteErrorStatus(status: number): boolean {
  return Number.isInteger(status) && status >= REFUSAL_STATUS_MIN && status <= REFUSAL_STATUS_MAX;
}

/** A reason phrase is a short human label; an unbounded one is a wire problem. */
const REASON_PHRASE_MAX_CHARS = 128;

/**
 * HTTP's reason-phrase grammar, as the `Response` constructor enforces it: HTAB,
 * SP, VCHAR (0x21–0x7E) and obs-text (0x80–0xFF). A newline, a NUL, or any
 * character outside Latin-1 — `'找不到'`, say — makes
 * `new Response(…, {statusText})` THROW, and that throw would land in exactly
 * the generic crash boundary this change removes, thrown by the code path whose
 * whole job is to render a clean 404.
 *
 * Dropping the offending characters rather than the whole field keeps the
 * STATUS — the part the boundary actually branches on — while making the
 * constructor call total.
 */
function reasonPhrase(statusText: string): string {
  let out = '';
  for (const ch of statusText) {
    const code = ch.codePointAt(0) ?? 0;
    if (code === 0x09 || (code >= 0x20 && code <= 0xff && code !== 0x7f)) out += ch;
    if (out.length >= REASON_PHRASE_MAX_CHARS) break;
  }
  return out;
}

/**
 * A loader's refusal, as it crosses the wire.
 *
 * The three fields are exactly the three `isRouteErrorResponse` exposes, so the
 * client can rebuild an error the router treats identically to the one SSR
 * would have produced for the same request.
 */
export interface DataRouteErrorFrame {
  readonly kind: typeof DATA_ROUTE_ERROR_KIND;
  /** The HTTP status the loader refused with (404, 403, 500, …). Always a
   *  refusal status — {@link dataRouteErrorFrame} is the only constructor. */
  readonly status: number;
  /** The status text, preserved so both legs render the same boundary copy.
   *  Always a legal HTTP reason phrase (see {@link reasonPhrase}). */
  readonly statusText: string;
  /** Whatever the loader attached to its `Response`; `null` when it attached nothing. */
  readonly data: unknown;
}

/**
 * Mint a frame. The only constructor — a bare object literal is not one — and
 * therefore the ONE place the two field invariants can be established:
 *
 *   * `status` is a refusal status. It used to be copied verbatim out of the
 *     loader's `ErrorResponse`, with the range check living only in the DECODER
 *     — i.e. on the far side of the wire, long after `bff.ts` had answered
 *     `c.json(frame, <that status>)`. A sub-400 status therefore produced a
 *     `200` carrying a frame: `res.ok` was true on the client, so nothing
 *     decoded anything, and the FRAME OBJECT itself was handed to the component
 *     as its loader data — the original destructure crash, reintroduced by the
 *     machinery that exists to prevent it. A status that cannot express a
 *     refusal degrades to 500, which is the honest reading of a refusal we
 *     cannot spell.
 *   * `statusText` is a legal HTTP reason phrase, so the client's
 *     `new Response(…, {statusText})` cannot throw on it.
 *
 * `data` is deliberately unconstrained: it is the loader's own payload, and the
 * only field here that is not part of the transport's grammar.
 */
export function dataRouteErrorFrame(
  status: number,
  statusText: string,
  data: unknown
): DataRouteErrorFrame {
  return {
    kind: DATA_ROUTE_ERROR_KIND,
    status: isDataRouteErrorStatus(status) ? status : UNSPELLABLE_REFUSAL_STATUS,
    statusText: reasonPhrase(statusText),
    data,
  };
}

/**
 * Read an untrusted response body as a frame, or `undefined`.
 *
 * Total by construction: every branch that is not a well-formed frame returns
 * `undefined`, so the caller's fallback (the raw HTTP status) is always
 * available. `status` is range-checked HERE TOO, and REJECTED rather than
 * coerced: unlike the mint, this side has a better answer available than 500 —
 * the status the transport actually delivered — so a forged or corrupted frame
 * costs the reader nothing but the frame. What it must never do is turn a
 * server error into a success on the client.
 */
export function decodeDataRouteErrorFrame(raw: unknown): DataRouteErrorFrame | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const candidate = raw as Partial<DataRouteErrorFrame>;
  if (candidate.kind !== DATA_ROUTE_ERROR_KIND) return undefined;
  const { status, statusText } = candidate;
  if (typeof status !== 'number' || !isDataRouteErrorStatus(status)) return undefined;
  if (typeof statusText !== 'string') return undefined;
  // Through the mint, so an untrusted `statusText` is reduced to a phrase the
  // client's `Response` constructor accepts. `?? null` (never `||`): `''` is the
  // DOMINANT `data` value on this wire — `throw new Response(null, …)` parses to
  // the empty string — and it is a value the loader chose, not an absence.
  return dataRouteErrorFrame(status, statusText, candidate.data ?? null);
}
